import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MapPin, Users, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomeGallery = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/properties`)
      .then((r) => setProperties(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#04271B]">
        <Loader2 className="w-8 h-8 text-[#D6B15E] animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#04271B]" data-testid="home-gallery">
      <Navbar />

      <header className="max-w-6xl mx-auto px-6 sm:px-10 pt-32 pb-14">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs tracking-[0.25em] uppercase text-[#D6B15E] mb-4"
        >
          Cheshire Holidays
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-heading font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-3xl"
        >
          Explore our retreats in immersive detail
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6 text-lg font-light text-[#A9B8A6] max-w-xl leading-relaxed"
        >
          Step inside each of our countryside properties before you book — take a video tour and look around in 360°.
        </motion.p>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {properties.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <Link
                to={`/property/${p.slug}`}
                data-testid={`property-card-${p.slug}`}
                className="group block overflow-hidden rounded-2xl border border-white/10 relative aspect-[4/3]"
              >
                <img
                  src={p.cover_image || p.hero_image}
                  alt={p.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <p className="text-[11px] tracking-[0.2em] uppercase text-[#D6B15E] flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> {p.location}
                  </p>
                  <h2 className="font-heading text-3xl sm:text-4xl font-light mt-2">{p.name}</h2>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm text-[#A9B8A6] flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#D6B15E]" strokeWidth={1.5} /> Sleeps {p.guests}
                    </span>
                    <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-white group-hover:text-[#D6B15E] transition-colors">
                      View tour <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>

      <footer className="border-t border-[#D6B15E]/20">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo-hut.png" alt="Cheshire Holidays" className="h-9 w-auto object-contain" />
            <span className="font-heading text-[#D6B15E] text-lg">Cheshire Holidays</span>
          </div>
          <p className="text-xs tracking-widest uppercase text-[#6f8378]">Immersive 360° Property Tours</p>
        </div>
      </footer>
    </div>
  );
};

export default HomeGallery;
